import React, {useState, useEffect, useCallback, useRef} from 'react'
import {createPortal} from 'react-dom'
import styled from '@emotion/styled'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import Calendar from 'react-calendar'
import 'react-calendar/dist/Calendar.css'
import {toast} from 'react-toastify'
import {useAuthClient} from '../utils/use-auth-client'

// ── Reuse ProposalTool design system ────────────────────────────────────────
import {theme} from '../pages/ProposalTool/features/Dashboard/theme'
import {
  ContentArea,
  MainArea,
  ModalOverlay,
  ModalBox,
  ModalActions,
  ModalCancelBtn,
  FormInput,
  FormLabel,
  FormField,
} from '../pages/ProposalTool/features/Dashboard/styled.components'
import {
  TableWrapper,
  StyledTable,
  THead,
  TBody,
  TR,
  TH,
  TD,
  ActionBtn,
  EmptyState,
  EmptyStateTitle,
} from '../pages/ProposalTool/components/ProposalTable/styled.components'
import {
  DateRangeWrapper,
  DateRangeTrigger,
  CalendarPopout,
} from '../pages/ProposalTool/components/ProposalFilters/styled.components'
import {ProposalPagination} from '../pages/ProposalTool/components/ProposalPagination'
import {PTView, PTCalender, PTCaretDown} from '../assets'

dayjs.extend(utc)
dayjs.extend(timezone)

const DEFAULT_TIMEZONE = 'America/Chicago'

const formatDate = dateStr =>
  dateStr
    ? dayjs(dateStr).tz(DEFAULT_TIMEZONE).format('MM/DD/YYYY hh:mmA z')
    : 'N/A'

const useDebouncedValue = (value, delay = 400) => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

// ── Status badge config ───────────────────────────────────────────────────────

const SUCCESS_CONFIG = {
  true: {bg: '#E8F5E9', color: '#2E7D32', label: 'Success'},
  false: {bg: '#fdecea', color: '#c62828', label: 'Failed'},
}

// ── Minimal new styled components ────────────────────────────────────────────

const ResultCount = styled.p`
  margin: 0 0 20px;
  font-size: clamp(11px, 1vw, 13px);
  color: ${theme.colors.muted};
  font-family: ${theme.font.family};
`

const FiltersRow = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  padding: 8px 12px;
`

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: ${theme.radius.full};
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  background: ${props =>
    (SUCCESS_CONFIG[props.success] || SUCCESS_CONFIG.false).bg};
  color: ${props =>
    (SUCCESS_CONFIG[props.success] || SUCCESS_CONFIG.false).color};
`

const DetailModalBox = styled(ModalBox)`
  max-width: 780px;
  width: 94%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-bottom: 0;
  background: #fff;
`

const ModalScrollContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-bottom: 24px;
  scrollbar-gutter: stable;
  min-height: 220px;
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${theme.colors.border};
`

const ModalTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 700;
  color: ${theme.colors.text};
  font-family: ${theme.font.family};
`

const ModalSubtitle = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${theme.colors.muted};
  font-family: ${theme.font.family};
`

const CloseBtn = styled.button`
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
  color: ${theme.colors.muted};
  line-height: 1;
  padding: 2px 6px;
  border-radius: ${theme.radius.sm};
  transition: background ${theme.transition.fast};

  &:hover {
    background: ${theme.colors.hover};
    color: ${theme.colors.text};
  }

  &:focus {
    outline: none;
  }
`

const ClearBtn = styled(ModalCancelBtn)`
  white-space: nowrap;
  background: #f4f6fb;
  border-color: #0327a9;
  color: #0327a9;

  &:hover {
    background: #e8ecf9;
  }
`

const DetailSection = styled.div`
  margin-bottom: 18px;
`

const DetailSectionTitle = styled.p`
  margin: 0 0 8px;
  font-family: ${theme.font.family};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${theme.colors.muted};
`

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 16px;
`

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const DetailLabel = styled.span`
  font-family: ${theme.font.family};
  font-size: 11px;
  color: ${theme.colors.muted};
`

const DetailValue = styled.span`
  font-family: ${theme.font.family};
  font-size: 13px;
  color: ${theme.colors.text};
  word-break: break-all;
`

const ErrorBox = styled.div`
  padding: 10px 14px;
  background: #fdecea;
  border: 1px solid #f5c6c6;
  border-radius: ${theme.radius.md};
  font-family: ${theme.font.family};
  font-size: 13px;
  color: #c62828;
  line-height: 1.5;
  word-break: break-word;
`

const SpinnerWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`

const Spinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid ${theme.colors.border};
  border-top-color: #0327a9;
  border-radius: 50%;
  animation: pe-spin 0.7s linear infinite;

  @keyframes pe-spin {
    to {
      transform: rotate(360deg);
    }
  }
`

// ── Date Range Picker ─────────────────────────────────────────────────────────

const DateRangePicker = ({startDate, endDate, onChange, onClear}) => {
  const [open, setOpen] = useState(false)
  const [selecting, setSelecting] = useState('start')
  const ref = useRef(null)

  useEffect(() => {
    const handler = e => {
      if (open && ref.current && !ref.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const handleDayClick = date => {
    if (selecting === 'start') {
      onChange({startDate: date, endDate: null})
      setSelecting('end')
    } else {
      onChange({
        startDate: dayjs(date).isBefore(dayjs(startDate)) ? date : startDate,
        endDate: dayjs(date).isBefore(dayjs(startDate)) ? startDate : date,
      })
      setSelecting('start')
      setOpen(false)
    }
  }

  const hasValue = !!(startDate || endDate)
  const label = hasValue
    ? startDate && endDate
      ? `${dayjs(startDate).format('MM/DD/YY')} — ${dayjs(endDate).format(
          'MM/DD/YY',
        )}`
      : `From ${dayjs(startDate).format('MM/DD/YY')}`
    : 'MM/DD/YY — MM/DD/YY'

  return (
    <FormField
      style={{flex: '1 1 200px', minWidth: '180px', position: 'relative'}}
    >
      <FormLabel>Date Range</FormLabel>
      <DateRangeWrapper ref={ref} style={{width: '100%'}}>
        <DateRangeTrigger
          type="button"
          hasValue={hasValue}
          onClick={() => {
            setOpen(o => !o)
            setSelecting('start')
          }}
        >
          <img
            src={PTCalender}
            width="13"
            alt=""
            style={{filter: 'brightness(0) invert(0.5)', flexShrink: 0}}
          />
          <span
            style={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </span>
          {hasValue ? (
            <span
              role="button"
              onClick={e => {
                e.stopPropagation()
                onClear()
                setOpen(false)
              }}
              style={{fontSize: '16px', lineHeight: 1, cursor: 'pointer'}}
            >
              &times;
            </span>
          ) : (
            <img
              src={PTCaretDown}
              width="10"
              alt=""
              style={{
                filter: 'brightness(0) invert(0.5)',
                flexShrink: 0,
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.15s ease',
              }}
            />
          )}
        </DateRangeTrigger>

        {open && (
          <CalendarPopout style={{left: 0, right: 'auto'}}>
            <p
              style={{
                margin: '8px 12px 4px',
                fontFamily: theme.font.family,
                fontSize: '12px',
                color: theme.colors.muted,
              }}
            >
              {selecting === 'start' ? 'Select start date' : 'Select end date'}
            </p>
            <Calendar
              onClickDay={handleDayClick}
              value={startDate || new Date()}
            />
          </CalendarPopout>
        )}
      </DateRangeWrapper>
    </FormField>
  )
}

// ── Detail Modal ─────────────────────────────────────────────────────────────

const DetailModal = ({record, loading, onClose}) => {
  if (!record && !loading) return null

  const successCfg = record
    ? SUCCESS_CONFIG[String(record.success)] || SUCCESS_CONFIG.false
    : null

  return createPortal(
    <ModalOverlay onClick={onClose}>
      <DetailModalBox onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <div>
            <ModalTitle>Payment Error Details</ModalTitle>
            {record && (
              <ModalSubtitle>
                Log ID: {record.id} &nbsp;&bull;&nbsp;{' '}
                {formatDate(record.created_date)}
              </ModalSubtitle>
            )}
          </div>
          <CloseBtn onClick={onClose}>&times;</CloseBtn>
        </ModalHeader>

        <ModalScrollContent>
          {loading ? (
            <SpinnerWrap>
              <Spinner />
            </SpinnerWrap>
          ) : record ? (
            <>
              {!record.success &&
                (record.error_message || record.error_type) && (
                  <DetailSection>
                    <DetailSectionTitle>Error</DetailSectionTitle>
                    <ErrorBox>
                      {record.error_type && (
                        <div>
                          <strong>Type:</strong> {record.error_type}
                          {record.error_code && ` (${record.error_code})`}
                        </div>
                      )}
                      {record.error_message && (
                        <div style={{marginTop: record.error_type ? 6 : 0}}>
                          <strong>Message:</strong> {record.error_message}
                        </div>
                      )}
                    </ErrorBox>
                  </DetailSection>
                )}

              <DetailSection>
                <DetailSectionTitle>Identity</DetailSectionTitle>
                <DetailGrid>
                  <DetailItem>
                    <DetailLabel>ID</DetailLabel>
                    <DetailValue>{record.id ?? '—'}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Recurly Account Code</DetailLabel>
                    <DetailValue>{record.thryv_id || '—'}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Email</DetailLabel>
                    <DetailValue>{record.user_email || '—'}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Order ID</DetailLabel>
                    <DetailValue>{record.order_id || '—'}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>User Role</DetailLabel>
                    <DetailValue>{record.user_role || '—'}</DetailValue>
                  </DetailItem>
                </DetailGrid>
              </DetailSection>

              <DetailSection>
                <DetailSectionTitle>API Call</DetailSectionTitle>
                <DetailGrid>
                  <DetailItem>
                    <DetailLabel>Endpoint</DetailLabel>
                    <DetailValue>{record.endpoint || '—'}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>HTTP Method</DetailLabel>
                    <DetailValue>{record.http_method || '—'}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Status Code</DetailLabel>
                    <DetailValue>{record.status_code ?? '—'}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Duration</DetailLabel>
                    <DetailValue>
                      {record.duration_ms != null
                        ? `${record.duration_ms}ms`
                        : '—'}
                    </DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Operation Type</DetailLabel>
                    <DetailValue>{record.operation_type || '—'}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Step Name</DetailLabel>
                    <DetailValue>{record.step_name || '—'}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>API Name</DetailLabel>
                    <DetailValue>{record.api_name || '—'}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Result</DetailLabel>
                    <DetailValue>
                      <StatusPill success={String(record.success)}>
                        {successCfg.label}
                      </StatusPill>
                    </DetailValue>
                  </DetailItem>
                </DetailGrid>
              </DetailSection>

              {(record.request_summary || record.response_summary) && (
                <DetailSection>
                  <DetailSectionTitle>Summary</DetailSectionTitle>
                  {record.request_summary && (
                    <DetailItem style={{marginBottom: 8}}>
                      <DetailLabel>Request</DetailLabel>
                      <DetailValue>{record.request_summary}</DetailValue>
                    </DetailItem>
                  )}
                  {record.response_summary && (
                    <DetailItem>
                      <DetailLabel>Response</DetailLabel>
                      <DetailValue>{record.response_summary}</DetailValue>
                    </DetailItem>
                  )}
                </DetailSection>
              )}

              {(record.request_payload || record.response_payload) && (
                <DetailSection>
                  <DetailSectionTitle>Payloads</DetailSectionTitle>
                  {record.request_payload && (
                    <DetailItem style={{marginBottom: 10}}>
                      <DetailLabel>Request Payload</DetailLabel>
                      <pre
                        style={{
                          margin: '4px 0 0',
                          padding: '10px 12px',
                          background: '#f4f6fb',
                          borderRadius: theme.radius.sm,
                          fontSize: '11px',
                          fontFamily: 'monospace',
                          color: theme.colors.text,
                          overflowX: 'auto',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                        }}
                      >
                        {typeof record.request_payload === 'string'
                          ? record.request_payload
                          : JSON.stringify(record.request_payload, null, 2)}
                      </pre>
                    </DetailItem>
                  )}
                  {record.response_payload && (
                    <DetailItem>
                      <DetailLabel>Response Payload</DetailLabel>
                      <pre
                        style={{
                          margin: '4px 0 0',
                          padding: '10px 12px',
                          background: '#f4f6fb',
                          borderRadius: theme.radius.sm,
                          fontSize: '11px',
                          fontFamily: 'monospace',
                          color: theme.colors.text,
                          overflowX: 'auto',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                        }}
                      >
                        {typeof record.response_payload === 'string'
                          ? record.response_payload
                          : JSON.stringify(record.response_payload, null, 2)}
                      </pre>
                    </DetailItem>
                  )}
                </DetailSection>
              )}
            </>
          ) : null}
        </ModalScrollContent>

        <ModalActions
          style={{
            padding: '16px 0 24px',
            borderTop: `1px solid ${theme.colors.border}`,
          }}
        >
          <ModalCancelBtn type="button" onClick={onClose}>
            Close
          </ModalCancelBtn>
        </ModalActions>
      </DetailModalBox>
    </ModalOverlay>,
    document.body,
  )
}

// ── Main Component ────────────────────────────────────────────────────────────

const ResizeHandle = styled.div`
  position: absolute;
  right: -4px;
  top: 15%;
  bottom: 15%;
  width: 10px;
  cursor: col-resize;
  user-select: none;
  z-index: 1;
  display: flex;
  align-items: stretch;
  justify-content: center;
  &::after {
    content: '';
    width: 2px;
    border-radius: 1px;
    background: rgba(0, 0, 0, 0.12);
    transition: background 0.15s ease;
  }
  &:hover::after {
    background: #0327a9;
  }
`

const COLUMNS = [
  {key: 'created_date', label: 'Timestamp'},
  {key: 'thryv_id', label: 'Recurly Account Code'},
  {key: 'account_email', label: 'Account Email'},
  {key: 'account_name', label: 'Account Name'},
  {key: 'operation_type', label: 'Operation'},
  {key: 'actions', label: ''},
]

const INIT_WIDTHS = [100, 100, 120, 100, 180, 50]

export function PaymentErrorsDashboard() {
  const client = useAuthClient()

  const [rows, setRows] = useState([])
  const [pagination, setPagination] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(15)

  const [emailFilter, setEmailFilter] = useState('')
  const [recurlyAccountCodeFilter, setRecurlyAccountCodeFilter] = useState('')
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [sortBy, setSortBy] = useState('')
  const [sortDir, setSortDir] = useState('desc')

  const [selectedRecord, setSelectedRecord] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const [colWidths, setColWidths] = useState(INIT_WIDTHS)
  const resizingRef = useRef(null)
  const tableRef = useRef(null)

  const autoFitColumn = useCallback(colIndex => {
    if (!tableRef.current) return
    const cells = tableRef.current.querySelectorAll(
      `td:nth-child(${colIndex + 1}), th:nth-child(${colIndex + 1})`,
    )
    let maxWidth = 0
    cells.forEach(cell => {
      if (cell.scrollWidth > maxWidth) maxWidth = cell.scrollWidth
    })
    if (maxWidth > 0) {
      setColWidths(prev => {
        const next = [...prev]
        next[colIndex] = maxWidth
        return next
      })
    }
  }, [])

  const startResize = useCallback(
    (e, colIndex) => {
      e.preventDefault()
      resizingRef.current = {
        colIndex,
        startX: e.clientX,
        startWidth: colWidths[colIndex],
      }
      const onMouseMove = ev => {
        if (!resizingRef.current) return
        const {colIndex: ci, startX, startWidth} = resizingRef.current
        const newWidth = Math.max(40, startWidth + (ev.clientX - startX))
        setColWidths(prev => {
          const next = [...prev]
          next[ci] = newWidth
          return next
        })
      }
      const onMouseUp = () => {
        resizingRef.current = null
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)
      }
      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
    },
    [colWidths],
  )

  const debouncedEmail = useDebouncedValue(emailFilter)
  const debouncedRecurlyAccountCode = useDebouncedValue(
    recurlyAccountCodeFilter,
  )

  const handleSort = key => {
    if (sortBy === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(key)
      setSortDir('asc')
    }
    setPage(1)
  }

  const fetchData = useCallback(async () => {
    setIsLoading(true)
    try {
      const params = new URLSearchParams()
      params.append('page', page)
      params.append('pageSize', perPage)
      if (debouncedEmail) params.append('email', debouncedEmail)
      if (debouncedRecurlyAccountCode)
        params.append('recurlyAccountCode', debouncedRecurlyAccountCode)
      if (startDate)
        params.append('startDate', dayjs(startDate).format('YYYY-MM-DD'))
      if (endDate)
        params.append(
          'endDate',
          dayjs(endDate).endOf('day').format('YYYY-MM-DD HH:mm:ss'),
        )
      if (sortBy) params.append('sortBy', sortBy)
      if (sortBy) params.append('sortDir', sortDir)

      const result = await client(`payment-errors?${params.toString()}`)
      setRows(result.response.data)
      setPagination(result.response.pagination)
    } catch {
      toast.error('Failed to load payment errors.')
    } finally {
      setIsLoading(false)
    }
  }, [
    client,
    page,
    perPage,
    debouncedEmail,
    debouncedRecurlyAccountCode,
    startDate,
    endDate,
    sortBy,
    sortDir,
  ])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleFilterChange = setter => e => {
    setter(e.target.value)
    setPage(1)
  }

  const handleViewDetails = useCallback(
    async logId => {
      setSelectedRecord(null)
      setDetailLoading(true)
      try {
        const result = await client(`payment-errors/${logId}`)
        setSelectedRecord(result.response)
      } catch {
        toast.error('Failed to load payment error details.')
      } finally {
        setDetailLoading(false)
      }
    },
    [client],
  )

  const handleCloseDetail = () => {
    setSelectedRecord(null)
    setDetailLoading(false)
  }

  const clearFilters = () => {
    setEmailFilter('')
    setRecurlyAccountCodeFilter('')
    setStartDate(null)
    setEndDate(null)
    setSortBy('')
    setSortDir('desc')
    setPage(1)
  }

  return (
    <ContentArea style={{padding: '12px 16px'}}>
      {pagination && pagination.total > 0 && (
        <ResultCount>
          {pagination.total} result{pagination.total !== 1 ? 's' : ''}
        </ResultCount>
      )}

      <MainArea style={{fontSize: 'clamp(11px, 1vw, 13px)'}}>
        <FiltersRow>
          <FormField style={{flex: '1 1 140px', minWidth: '120px'}}>
            <FormLabel>Recurly Account Code</FormLabel>
            <FormInput
              type="text"
              placeholder="Search..."
              value={recurlyAccountCodeFilter}
              onChange={handleFilterChange(setRecurlyAccountCodeFilter)}
            />
          </FormField>

          <FormField style={{flex: '1 1 160px', minWidth: '140px'}}>
            <FormLabel>Account Email</FormLabel>
            <FormInput
              type="text"
              placeholder="Search by email..."
              value={emailFilter}
              onChange={handleFilterChange(setEmailFilter)}
            />
          </FormField>

          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onChange={({startDate: s, endDate: e}) => {
              setStartDate(s)
              setEndDate(e)
              setPage(1)
            }}
            onClear={() => {
              setStartDate(null)
              setEndDate(null)
              setPage(1)
            }}
          />

          <div style={{display: 'flex', alignItems: 'flex-end'}}>
            <ClearBtn type="button" onClick={clearFilters}>
              Clear Filters
            </ClearBtn>
          </div>
        </FiltersRow>

        <TableWrapper>
          <StyledTable
            ref={tableRef}
            style={{
              tableLayout: 'fixed',
              width: colWidths.reduce((a, b) => a + b, 0),
            }}
          >
            <THead>
              <TR>
                {COLUMNS.map((col, i) => (
                  <TH
                    key={col.key}
                    onDoubleClick={
                      col.key !== 'actions' ? () => autoFitColumn(i) : undefined
                    }
                    style={{
                      width: colWidths[i],
                      position: 'relative',
                      userSelect: 'none',
                    }}
                  >
                    {col.key !== 'actions' ? (
                      <span
                        onClick={() => handleSort(col.key)}
                        style={{
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                        }}
                      >
                        <span
                          style={{
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {col.label}
                        </span>
                        <span
                          style={{
                            flexShrink: 0,
                            fontSize: '10px',
                            opacity: sortBy === col.key ? 1 : 0.35,
                            color: sortBy === col.key ? '#0327a9' : 'inherit',
                          }}
                        >
                          {sortBy === col.key
                            ? sortDir === 'asc'
                              ? '↑'
                              : '↓'
                            : '⇅'}
                        </span>
                      </span>
                    ) : null}
                    {col.key !== 'actions' && (
                      <ResizeHandle onMouseDown={e => startResize(e, i)} />
                    )}
                  </TH>
                ))}
              </TR>
            </THead>
            <TBody>
              {isLoading ? (
                Array.from({length: 5}).map((_, i) => (
                  <TR key={`sk-${i}`}>
                    {COLUMNS.map(col => (
                      <TD key={col.key}>
                        <div
                          style={{
                            height: '14px',
                            borderRadius: '4px',
                            background:
                              'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                            backgroundSize: '200% 100%',
                            animation: 'skeleton-shimmer 1.5s infinite',
                            width: col.key === 'actions' ? '60px' : '80%',
                          }}
                        />
                      </TD>
                    ))}
                  </TR>
                ))
              ) : rows.length === 0 ? (
                <TR>
                  <TD colSpan={COLUMNS.length}>
                    <EmptyState>
                      <EmptyStateTitle>
                        No payment errors found.
                      </EmptyStateTitle>
                    </EmptyState>
                  </TD>
                </TR>
              ) : (
                rows.map((row, idx) => (
                  <TR key={row.id || idx}>
                    <TD
                      style={{
                        whiteSpace: 'nowrap',
                        fontSize: '12px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {formatDate(row.created_date)}
                    </TD>
                    <TD
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={row.thryv_id || ''}
                    >
                      {row.thryv_id || '—'}
                    </TD>
                    <TD
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={row.account_email || ''}
                    >
                      {row.account_email || '—'}
                    </TD>
                    <TD
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={row.account_name?.trim() || ''}
                    >
                      {row.account_name?.trim() || '—'}
                    </TD>
                    <TD
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={row.operation_type || ''}
                    >
                      {row.operation_type || '—'}
                    </TD>
                    <TD>
                      <ActionBtn onClick={() => handleViewDetails(row.id)}>
                        <img src={PTView} width="13" alt="" />
                      </ActionBtn>
                    </TD>
                  </TR>
                ))
              )}
            </TBody>
          </StyledTable>
        </TableWrapper>

        {pagination && (
          <ProposalPagination
            currentPage={pagination.page}
            totalItems={pagination.total}
            perPage={perPage}
            onPageChange={setPage}
            onPerPageChange={value => {
              setPerPage(value)
              setPage(1)
            }}
          />
        )}
      </MainArea>

      <DetailModal
        record={selectedRecord}
        loading={detailLoading}
        onClose={handleCloseDetail}
      />

      <style>{`
        @keyframes skeleton-shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </ContentArea>
  )
}
